import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Button, Select, MenuItem, Typography, FormControlLabel, Checkbox, Box, Link, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, TextField } from '@mui/material';
import { createGlobalStyle } from 'styled-components';
import storefrontImage from './assets/storefront.jpg';
import mapImage from './assets/map.jpg';
import logoImage from './assets/logo.png';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  'https://mac-offer-backend-test-f8d809a394fb.herokuapp.com';

  const GlobalStyle = createGlobalStyle`
  html, body {
    margin: 0;
    padding: 0;
    height: auto;
    min-height: 100vh;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    touch-action: auto !important;
    pointer-events: auto !important;
  }
`;

// OfferPage Component
// OfferPage Component
function OfferPage() {
  console.log('Rendering OfferPage');
  const { state } = useLocation();
  const navigate = useNavigate();
  const { offerPrice, deviceDetails } = state || {};
  const [requestEmail, setRequestEmail] = useState('');
  const isRequestFlow = state?.requestFlow === true;
  
  // Lock-In Quote dialog & feedback state
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockEmail, setLockEmail] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [lockSent, setLockSent] = useState(false);

  // Ensure scrollability and handle state check
  useEffect(() => {
    console.time('OfferPageLoad');
    if (!state) {
      console.log('OfferPage: No state found, navigating to home.');
      navigate('/');
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.touchAction = 'auto';
    }
    console.timeEnd('OfferPageLoad');
  }, [state, navigate]);

  // Scroll to top on initial mount only
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Send iframe height to parent
  useEffect(() => {
    const sendHeight = () => {
      const height = document.body.scrollHeight;
      console.log('OfferPage: Sending iframe height:', height);
      window.parent.postMessage({ iframeHeight: height }, '*');
    };
    sendHeight();
    window.addEventListener('resize', sendHeight);
    const interval = setInterval(sendHeight, 100);
    return () => {
      window.removeEventListener('resize', sendHeight);
      clearInterval(interval);
    };
  }, [state, lockDialogOpen, snackbarOpen, requestSent, lockSent]);

  const handleLockClick = () => {
    setLockDialogOpen(true);
  };
  
  const handleSendLock = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/lock-in-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: lockEmail,
          deviceDetails: {
            product: deviceDetails.product,
            cpu: deviceDetails.cpu,
            screenSize: deviceDetails.screenSize,
            ssd: deviceDetails.ssd,
            ram: deviceDetails.ram,
            radio: deviceDetails.radio,
            condition: deviceDetails.condition,
            functionalCondition: deviceDetails.functionalCondition,
            issues: deviceDetails.issues,
            adapter: deviceDetails.adapter,
            batteryCondition: deviceDetails.batteryCondition,
            conditionDescription: deviceDetails.conditionDescription,
            mpn: deviceDetails.mpn,
          },
          offerPrice
        })
      });
      if (response.ok) {
        setSnackbarMessage('Quote locked! Check your email!');
        setLockSent(true);
      } else {
        setSnackbarMessage('Failed to lock quote. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setSnackbarMessage('Error locking quote.');
    } finally {
      setSnackbarOpen(true);
      setLockDialogOpen(false);
    }
  };
  
  const handleRequestQuote = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/lock-in-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: requestEmail, deviceDetails, offerPrice: '' })
      });
      setSnackbarMessage(res.ok
        ? 'Quote request sent! Most quotes are sent within one business day.'
        : 'Failed to request quote. Please try again.'
      );
      if (res.ok) setRequestSent(true);
    } catch (err) {
      console.error('Request quote error:', err);
      setSnackbarMessage('Error requesting quote.');
    } finally {
      setSnackbarOpen(true);
    }
  };

  if (!state) {
    return null;
  }

  // Create details list with descriptive labels
  const isPhoneTablet = ['iPhone', 'iPad'].includes(deviceDetails.product);
  const detailsList = [
    // Product-specific details
    { label: 'Model', value: deviceDetails.product },
    ...(isPhoneTablet
      ? [
          { label: 'Model Details', value: deviceDetails.cpu },
          { label: 'Capacity', value: deviceDetails.ssd },
          { label: deviceDetails.product === 'iPad' ? 'Connectivity' : 'Carrier', value: deviceDetails.radio },
        ]
      : [
          { label: 'CPU', value: deviceDetails.cpu },
          deviceDetails.screenSize && { label: 'Screen Size', value: `${deviceDetails.screenSize}"` },
          { label: 'Storage', value: deviceDetails.ssd },
          deviceDetails.ram && { label: 'Memory', value: deviceDetails.ram },
        ]
    ),
    // Functionality-related details (if not in request flow)
    ...(isRequestFlow
      ? []
      : [
          {
            label: 'Functionality',
            value: deviceDetails.functionalCondition === '100% Functional' ? 'Fully Functional' : 'Has Issues',
          },
          ...(deviceDetails.issues?.length > 0
            ? [{ label: 'Issues', value: deviceDetails.issues.join(', ') }]
            : []),
          ...(deviceDetails.adapter
            ? [{ label: 'Original Charger', value: deviceDetails.adapter.includes('Included') ? 'Included' : 'Not Included' }]
            : []),
          ...(isPhoneTablet && deviceDetails.batteryCondition
            ? [{ label: 'Battery', value: deviceDetails.batteryCondition }]
            : []),
        ]
    ),
    // Cosmetic condition details
    { label: 'Cosmetic Condition', value: deviceDetails.condition },
    deviceDetails.conditionDescription && { label: 'Description', value: deviceDetails.conditionDescription },
    // MPN if provided
    deviceDetails.mpn && { label: 'MPN/Part Number', value: deviceDetails.mpn },
  ].filter(Boolean); // Remove falsy entries

  return (
    <Box sx={{ padding: '20px', width: '100%', margin: '0 auto', '@media (max-width: 767px)': { maxWidth: '100% !important', padding: '10px' } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              padding: { xs: '24px', md: '32px' },
              textAlign: 'center',
            }}
          >
            {!isRequestFlow && offerPrice !== 'Not Found' && (
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '24px', md: '28px' },
                    fontWeight: 700,
                    color: '#1a1a1a',
                    mb: 3,
                    letterSpacing: '-0.01em',
                  }}
                >
                  WE PAY CASH ON THE SPOT
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1a1a1a',
                    mb: 1,
                  }}
                >
                  130 West 26th St, New York, NY 10001
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '16px',
                    color: '#666',
                    lineHeight: 1.6,
                  }}
                >
                  Bring your device to our Manhattan retail store, and we'll pay you cash in just 20 minutes.  No appointment required for selling a single device.<br /> 
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                width: '80%',
                height: '2px',
                backgroundColor: '#e0e0e0',
                margin: '0 auto 32px',
              }}
            />
            <Box
              component={!isRequestFlow && offerPrice !== 'Not Found' ? 'a' : 'div'}
              href={!isRequestFlow && offerPrice !== 'Not Found' ? "https://app.acuityscheduling.com/schedule/10b8e4af/appointment/14612935/calendar/4019366" : undefined}
              target={!isRequestFlow && offerPrice !== 'Not Found' ? "_blank" : undefined}
              rel={!isRequestFlow && offerPrice !== 'Not Found' ? "noopener noreferrer" : undefined}
              sx={{
                background: isRequestFlow
                  ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                  : (offerPrice === 'Not Found'
                     ? 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)'
                     : 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                    ),
                borderRadius: '16px',
                padding: { xs: '24px 16px', md: '32px 24px' },
                mb: 4,
                position: 'relative',
                overflow: 'hidden',
                display: 'block',
                textDecoration: 'none',
                cursor: !isRequestFlow && offerPrice !== 'Not Found' ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': !isRequestFlow && offerPrice !== 'Not Found' ? {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                } : {},
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: isRequestFlow
                    ? '#1976d2'
                    : (offerPrice === 'Not Found' ? '#d32f2f' : '#2e7d32'),
                },
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '40px', md: '56px' },
                  fontWeight: 800,
                  color: isRequestFlow
                    ? '#1565c0'
                    : (offerPrice === 'Not Found' ? '#c62828' : '#1b5e20'),
                  mb: 0,
                  textAlign: 'center',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                {isRequestFlow
                  ? 'Request A Quote'
                  : (offerPrice === 'Not Found'
                     ? 'Offer Not Available'
                     : offerPrice
                    )
                }
              </Typography>
              {!isRequestFlow && offerPrice !== 'Not Found' && (
                <>
                  <Typography
                    sx={{
                      fontSize: '18px',
                      fontWeight: 500,
                      color: '#1b5e20',
                      textAlign: 'center',
                      mt: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    Cash Offer
                  </Typography>
                </>
              )}
            </Box>
            
            {isRequestFlow && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                {requestSent ? (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#10b981',
                      color: '#fff',
                      fontSize: '16px',
                      fontWeight: 600,
                      height: '48px',
                      borderRadius: '8px',
                      textTransform: 'none',
                      width: '100%',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                      '&:hover': { backgroundColor: '#10b981' },
                    }}
                    disableElevation
                  >
                    ✓ Quote Requested!
                  </Button>
                ) : (
                  <>
                    <TextField
                      label="Email Address"
                      type="email"
                      fullWidth
                      value={requestEmail}
                      onChange={e => setRequestEmail(e.target.value)}
                      sx={{ mb: 0 }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', mt: 2 }}>
                      Most quotes are sent out within one business day
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleRequestQuote}
                      disabled={!requestEmail}
                      sx={{
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        fontSize: '16px',
                        fontWeight: 600,
                        height: '48px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        mt: 3,
                        width: '100%',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#999',
                        },
                      }}
                    >
                      Request Quote
                    </Button>
                  </>
                )}
                <Snackbar
                  open={snackbarOpen}
                  autoHideDuration={6000}
                  onClose={() => setSnackbarOpen(false)}
                  message={snackbarMessage}
                />
                
                <Box
                  sx={{
                    width: '80%',
                    height: '2px',
                    backgroundColor: '#e0e0e0',
                    margin: '32px auto',
                  }}
                />
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '24px', md: '28px' },
                      fontWeight: 700,
                      color: '#1a1a1a',
                      mb: 3,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    WE PAY CASH ON THE SPOT
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#1a1a1a',
                      mb: 1,
                    }}
                  >
                    130 West 26th St, New York, NY 10001
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '16px',
                      color: '#666',
                      lineHeight: 1.6,
                    }}
                  >
                    If you can't wait for a quote you can bring your device to our Manhattan retail store, and we'll give you an on the spot quote and pay you cash in just 20 minutes.
                  </Typography>
                </Box>
                
                <Box
                  sx={{
                    width: '80%',
                    height: '2px',
                    backgroundColor: '#e0e0e0',
                    margin: '32px auto',
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                background: '#f8fafc',
                borderRadius: '12px',
                padding: '24px',
                mb: 4,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {detailsList.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: index < detailsList.length - 1 ? '1px solid #e0e0e0' : 'none',
                      pb: index < detailsList.length - 1 ? 2 : 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#64748b',
                        flex: '0 0 auto',
                        pr: 2,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#1a1a1a',
                        textAlign: 'right',
                        wordBreak: 'break-word',
                        flex: 1,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {!isRequestFlow && (
              <>
                <Dialog open={lockDialogOpen} onClose={() => setLockDialogOpen(false)}>
                  <DialogTitle>Lock In Your Quote For 14 Days</DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Enter your email below to receive an email quote and lock in this price.
                    </Typography>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="Email Address"
                      type="email"
                      fullWidth
                      value={lockEmail}
                      onChange={e => setLockEmail(e.target.value)}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => setLockDialogOpen(false)}
                      sx={{
                        color: '#666',
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSendLock}
                      disabled={!lockEmail}
                      variant="contained"
                      sx={{
                        backgroundColor: '#1976d2',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0',
                        },
                      }}
                    >
                      Send Quote
                    </Button>
                  </DialogActions>
                </Dialog>

                <Snackbar
                  open={snackbarOpen}
                  autoHideDuration={6000}
                  onClose={() => setSnackbarOpen(false)}
                  message={snackbarMessage}
                />

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    color: '#888',
                  }}
                >
                  Offers are updated every Sunday. .<br /> 
                  Lock-in quote for 14 days below:
                </Typography>
                
                {lockSent ? (
                  <Button
                    variant="contained"
                    disableElevation
                    sx={{
                      backgroundColor: '#10b981',
                      color: '#fff',
                      fontSize: '16px',
                      fontWeight: 600,
                      height: '48px',
                      borderRadius: '8px',
                      textTransform: 'none',
                      width: '100%',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                      '&:hover': { backgroundColor: '#10b981' },
                    }}
                  >
                    ✓ Quote Locked! Check your email!
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={handleLockClick}
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      height: '56px',
                      borderRadius: '12px',
                      borderWidth: '2px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        borderWidth: '2px',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography component="span" sx={{ fontSize: '16px', fontWeight: 600 }}>
                        Lock In Quote
                      </Typography>
                      <Typography component="span" sx={{ fontSize: '13px', fontWeight: 400 }}>
                        For 14 Days
                      </Typography>
                    </Box>
                  </Button>
                )}
              </>
            )}
          </Box>

          <Box
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Link
              href="https://maps.app.goo.gl/w44uQ2bgQTFbi9QL6"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'block',
                '&:hover': {
                  opacity: 0.9,
                  transition: 'opacity 0.3s ease',
                },
              }}
            >
              <img
                src={mapImage}
                alt="Map to Computer Overhauls - View on Google Maps"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Link>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Link
              href="https://maps.app.goo.gl/w44uQ2bgQTFbi9QL6"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'block',
                '&:hover': {
                  opacity: 0.9,
                  transition: 'opacity 0.3s ease',
                },
              }}
            >
              <img
                src={storefrontImage}
                alt="Computer Overhauls Storefront - View on Google Maps"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Link>
          </Box>

          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              padding: { xs: '24px', md: '32px' },
            }}
          >
            
            <Box mb={2}>
              <Typography variant="subtitle1" sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                 Store Hours<br /> 
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '15px', color: '#666', lineHeight: 1.6 }}>
                Walk-in anytime during these hours:<br />
                Mon, Tue, Wed, Fri: 12:00PM-7:30PM<br />
                Saturday: 12:00PM-4:30PM<br />
                Closed Thu and Sun
              </Typography>
            </Box>
                   <Box mb={2}>
              <Typography variant="subtitle1" sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                Appointments
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '15px', color: '#666', lineHeight: 1.6 }}>
                Appointments are not required for selling a single device, but are highly recommended for multiple devices.{' '}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle1" sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                Payment Options
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '15px', color: '#666', lineHeight: 1.6 }}>
                Cash, Venmo, or PayPal.
              </Typography>
            </Box>

            <Box mb={0}>
              <Typography variant="subtitle1" sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                Before You Come
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '15px', color: '#666', lineHeight: 1.6 }}>
                Read our{' '}
                <Link
                  href="https://computeroverhauls.com/plan-your-visit.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#1976d2',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Plan Your Visit
                </Link>{' '}
                page for important instructions on how to prepare your device to be sold.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          sx={{
            fontSize: '16px',
            padding: '10px 20px',
            borderRadius: '8px',
            textTransform: 'none',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              borderColor: '#1565c0',
            },
          }}
        >
          Start Over / Check Another Device
        </Button>
      </Box>
    </Box>
  );
}

// App Component (Main Form Logic)
function App() {
  console.log('Rendering App component');
  const location = useLocation();
  const navigate = useNavigate();

  // State Hooks
  const [product, setProduct] = useState('');
  const [configs, setConfigs] = useState([]);
  const [screenSize, setScreenSize] = useState('');
  const [year, setYear] = useState('');
  const [cpu, setCpu] = useState('');
  const [ssd, setSsd] = useState('');
  const [ram, setRam] = useState('');
  const [radio, setRadio] = useState('');
  const [condition, setCondition] = useState('');
  const [functionalCondition, setFunctionalCondition] = useState('');
  const [issues, setIssues] = useState([]);
  const [adapter, setAdapter] = useState('');
  const [batteryCondition, setBatteryCondition] = useState('');
  const [mpn, setMpn] = useState('');
  const [offer, setOffer] = useState(null);
  const [isProductPreselected, setIsProductPreselected] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);

  // Send iframe height to parent
  useEffect(() => {
    const sendHeight = () => {
      const height = document.body.scrollHeight;
      console.log('App: Sending iframe height:', height);
      window.parent.postMessage({ iframeHeight: height }, '*');
    };
    sendHeight();
    window.addEventListener('resize', sendHeight);
    const interval = setInterval(sendHeight, 100);
    return () => {
      window.removeEventListener('resize', sendHeight);
      clearInterval(interval);
    };
  }, [
    product,
    configs,
    year,
    cpu,
    screenSize,
    ssd,
    ram,
    radio,
    condition,
    functionalCondition,
    issues,
    adapter,
    batteryCondition,
    isLoadingConfigs
  ]);

  const products = [
    'iPhone', 'iPad', 'MacBook Air', 'iMac', 'MacBook Pro'
  ];

  const macIssueOptions = [ 'No power and/or No Boot', 'Keyboard', 'WiFi and/or Bluetooth', 'Bad Battery', 'iCloud Locked', 'USB or Other Port', 'Trackpad', 'Liquid Damage', 'Screen Issue(s)', 'Non-US / Foreign Keyboard', 'My issue is not listed' ];
  const phoneTabletIssueOptions = [ 'No power', 'iCloud Locked', 'Liquid Damage', 'Screen Issue(s)', 'Speaker or Earpiece', 'Camera', 'Non-functional Button(s)', 'Does not make a call', 'My issue is not listed' ];

  const carrierOptions = ['Unlocked', 'AT&T', 'Verizon', 'Sprint', 'T-Mobile'];
  const connectivityOptions = ['Wifi Only', 'Cellular + Wifi'];

  const batteryConditionOptions = [
    'Healthy Battery - at least 80% capacity - no warnings',
    'Weak Battery - 79% or less capacity or "Service" / "Replace" warning'
  ];

  const conditionDescriptionsByGroup = {
    phoneTablet: {
      'Brand New, Factory Sealed': 'Unopened in original packaging, factory sealed. If the box has been opened even once please select flawless. MDM and iCloud clear.',
      Flawless: 'Absolutely mint condition, as if it were brand new. MDM and iCloud clear.',
      Good: 'Minor signs of use with no functional issues. MDM and iCloud clear.',
      Fair: 'Visible wear or scratches but fully functional. MDM and iCloud clear.',
      Poor: 'Heavy wear or damage that may affect appearance.',
    },
    macBooks: {
      'Brand New, Factory Sealed': 'Unopened in original packaging, factory sealed. If the box has been opened even once please select flawless. MDM and iCloud clear',
      Flawless: 'Absolutely perfect condition as if it was brand new out of the box. Not a single scratch, nick, scuff, ding, sticker or defect anywhere on the device with a US keyboard, MDM and iCloud clear. From a non-smoking environment free of any odor.',
      Good: 'Having light wear. This includes having very minor nicks or light scratches. From a non-smoking environment free of any odor. No dents, cracks, chips, damaged corners, stickers, etc. MDM and iCloud clear.',
      Fair: 'Fully functional but with significant cosmetic wear. This includes stickers, scratches, dings, dents, minor cracks, or chips of the body. No cracks on the screen. US keyboard, fully functional, MDM and iCloud clear.',
      Poor: 'Significant cosmetic damage or cracked screen; must still work when connected to an external monitor. MDM and iCloud clear.',
    },
    miniStudio: {
      'Brand New, Factory Sealed': 'Unopened in original packaging, factory sealed. If the box has been opened even once please select flawless.',
      Flawless: 'Chassis looks new with no marks; internal components spotless.',
      Good: 'Light scuffs on the shell; performance unaffected.',
      Fair: 'Dings or scratches on the case; works as expected.',
      Poor: 'Extensive case damage; operation may be impacted.',
    },
    iMac: {
      'Brand New, Factory Sealed': 'Unopened in original packaging, factory sealed. If the box has been opened even once please select flawless. MDM and iCloud clear.',
      Flawless: 'Screen and stand pristine; looks factory new. MDM and iCloud clear.',
      Good: 'Small scratches on the back, stand or bezel. No scratches on the screen. No screen defects, hotspots, dead pixels or dust in the corners. MDM and iCloud clear.',
      Fair: 'Noticeable bezel or stand damage; runs fine. MDM and iCloud clear.',
      Poor: 'Screen dents or major stand dings; may affect stability.',
    },
    visionPro: {
      'Brand New, Factory Sealed': 'Unopened in original packaging, factory sealed. If the box has been opened even once please select flawless.',
      Flawless: 'No marks on lenses or headband; like new. MDM and iCloud clear.',
      Good: 'Minor strap wear; optics clear and functional. MDM and iCloud clear.',
      Fair: 'Scuffs on housing; all features work. MDM and iCloud clear.',
      Poor: 'Damaged strap or casing; functionality may be limited.',
    },
  };

  const conditionDescriptions = (() => {
    if (['iPhone', 'iPad'].includes(product)) {
      return conditionDescriptionsByGroup.phoneTablet;
    }
    if (['MacBook Pro', 'MacBook Air'].includes(product)) {
      return conditionDescriptionsByGroup.macBooks;
    }
    if (['Mac Mini', 'Mac Studio'].includes(product)) {
      return conditionDescriptionsByGroup.miniStudio;
    }
    if (product === 'iMac') {
      return conditionDescriptionsByGroup.iMac;
    }
    if (product === 'Vision Pro') {
      return conditionDescriptionsByGroup.visionPro;
    }
    return conditionDescriptionsByGroup.macBooks;
  })();

  useEffect(() => {
    console.log('Checking URL for product parameter...');
    const queryParams = new URLSearchParams(location.search);
    const productFromUrl = queryParams.get('product');
    console.log('Product from URL:', productFromUrl);

    const isValidProduct = productFromUrl && products.includes(productFromUrl);
    console.log('Is valid product?', isValidProduct);

    if (isValidProduct) {
      if (product !== productFromUrl) {
          console.log(`Setting product from URL: ${productFromUrl}`);
          setProduct(productFromUrl);
          setIsProductPreselected(true);
          resetSubsequentFields('product');
      } else {
          setIsProductPreselected(true);
      }
    } else {
      setIsProductPreselected(false);
    }
  }, [location.search]);

  useEffect(() => {
    if (!product) {
      console.log('Product cleared, clearing configs.');
      setConfigs([]);
      return;
    }

    console.log(`Fetching configs for product: ${product}`);
    setIsLoadingConfigs(true);
    setConfigs([]);

    const fetchUrl = `${BACKEND_URL}/configs?product=${encodeURIComponent(product)}`;
    console.log('Fetch URL:', fetchUrl);

    fetch(fetchUrl)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (!Array.isArray(data)) {
            console.error('Config fetch error: Received non-array data:', data);
            throw new Error('Invalid data format received from server.');
        }
        const validConfigs = data.filter(row =>
            Array.isArray(row) && row.length >= 1 && typeof row[0] === 'string' && row[0].trim() !== ''
        );
        console.log('Raw config data received:', data);
        console.log('Filtered valid configs:', validConfigs);
        setConfigs(validConfigs);
      })
      .catch(err => {
        console.error('Config fetch error:', err);
        setConfigs([]);
      })
      .finally(() => {
        setIsLoadingConfigs(false);
      });
  }, [product]);

  const isMac = ['MacBook Pro', 'MacBook Air', 'Mac Mini', 'Mac Studio', 'iMac'].includes(product);
  const isPhoneTablet = ['iPhone', 'iPad'].includes(product);
  const isIPad = product === 'iPad';
  const isIPhone = product === 'iPhone';
  const hasScreen = ['MacBook Pro', 'MacBook Air', 'iMac', 'iPad'].includes(product);

  // Auto-select "Unlocked" carrier when iPhone 16 or 17 is selected
  useEffect(() => {
    if (isIPhone && cpu) {
      const cpuStr = String(cpu);
      const isIPhone16or17 = /iPhone\s+(16|17)(\s|$)/i.test(cpuStr);
      if (isIPhone16or17 && radio !== 'Unlocked') {
        console.log('iPhone 16/17 detected - auto-selecting Unlocked carrier');
        setRadio('Unlocked');
      }
    }
  }, [cpu, isIPhone, radio]);

  const parseConfig = (configString) => {
    if (!configString || typeof configString !== 'string' || !configString.includes('-')) {
      console.warn('Invalid config string format:', configString);
      return { year: '', screenSize: '', cpu: '', ssd: '', ram: '', radio: '' };
    }
    const parts = configString.split('-');
    try {
      if (isMac) {
        if (hasScreen) {
          const year = parts[0];
          const screenSize = parts[2]?.replace(/"/g, '');
          const cpu = parts[parts.length === 7 ? 3 : 3];
          const ram = parts[parts.length === 7 ? 4 : 4];
          const ssd = parts[parts.length === 7 ? 5 : 5];
          const gpuCores = parts.length === 7 ? parts[6] : '';
          const fullCpu = gpuCores ? `${cpu}-${gpuCores}` : cpu;
          return { year, screenSize, cpu: fullCpu, ssd, ram, radio: '' };
        } else {
          const year = parts[0];
          const cpu = parts[parts.length === 6 ? 2 : 2];
          const ram = parts[parts.length === 6 ? 3 : 3];
          const ssd = parts[parts.length === 6 ? 4 : 4];
          const gpuCores = parts.length === 6 ? parts[5] : '';
          const fullCpu = gpuCores ? `${cpu}-${gpuCores}` : cpu;
          return { year, screenSize: '', cpu: fullCpu, ssd, ram, radio: '' };
        }
      } else if (isIPad) {
        if (parts.length === 4) {
          const [cpu, screenSize, ssd, radio] = parts;
          return { year: '', screenSize, cpu, ssd, ram: '', radio };
        }
      } else if (isIPhone) {
        if (parts.length === 3) {
          const [cpu, ssd, radio] = parts;
          return { year: '', screenSize: '', cpu, ssd, ram: '', radio };
        }
      }
      console.warn('Unrecognized config format for product type:', product, configString);
      return { year: '', screenSize: '', cpu: '', ssd: '', ram: '', radio: '' };
    } catch (error) {
        console.error("Error parsing config:", configString, error);
        return { year: '', screenSize: '', cpu: '', ssd: '', ram: '', radio: '' };
    }
  };

  const sortIPhoneModels = (models) => {
    // Custom sorting function for iPhone models to display in chronological order
    const getIPhoneSortValue = (model) => {
      const modelStr = String(model);

      // Map special iPhone models to numeric equivalents for sorting
      const specialModels = {
        'SE': 6.5,          // iPhone SE (1st gen, between 6S and 7)
        'SE 2': 11.5,       // iPhone SE (2nd gen)
        'SE 3': 14.5,       // iPhone SE (3rd gen)
        'X': 8.5,           // iPhone X (came after 8)
        'XR': 10,           // iPhone XR
        'XS': 10.5,         // iPhone XS
        'XS Max': 10.6      // iPhone XS Max
      };

      // Variant order within each generation (lower number = earlier in list)
      const variantOrder = {
        'mini': 1,
        'base': 2,      // No suffix
        'Plus': 3,
        'Pro': 4,
        'Pro Max': 5
      };

      // Check for special models first
      for (const [key, value] of Object.entries(specialModels)) {
        if (modelStr.includes(key)) {
          return value * 10; // Multiply by 10 to leave room for variants
        }
      }

      // Extract numeric generation (e.g., "iPhone 12 Pro Max" -> 12)
      const genMatch = modelStr.match(/iPhone\s+(\d+)/);
      if (!genMatch) return 0; // Fallback for unknown format

      const generation = parseInt(genMatch[1], 10);

      // Determine variant
      let variant = 'base';
      if (modelStr.includes('Pro Max')) variant = 'Pro Max';
      else if (modelStr.includes('Pro')) variant = 'Pro';
      else if (modelStr.includes('Plus')) variant = 'Plus';
      else if (modelStr.includes('mini')) variant = 'mini';

      // Return combined sort value: generation * 10 + variant order
      // This ensures iPhone 12 mini (120 + 1) comes before iPhone 12 Pro Max (120 + 5)
      return generation * 10 + (variantOrder[variant] || 2);
    };

    return models.sort((a, b) => getIPhoneSortValue(a) - getIPhoneSortValue(b));
  };

  const sortAppleSiliconProcessors = (processors) => {
    // Custom sorting function for Apple Silicon processors (M1, M2, M3, M4, etc.)
    const getAppleSiliconSortValue = (processor) => {
      const procStr = String(processor);

      // Check if this is Apple Silicon (starts with M followed by a number)
      const appleMatch = procStr.match(/^M(\d+)\s*(Pro|Max|Ultra)?(?:-(\d+)C)?/i);
      if (!appleMatch) {
        // Not Apple Silicon (probably Intel), return high value to sort alphabetically at end
        return 999999 + procStr.charCodeAt(0);
      }

      const generation = parseInt(appleMatch[1], 10); // M1=1, M2=2, M3=3, etc.
      const variantStr = appleMatch[2] || 'base';     // Pro, Max, Ultra, or base (no suffix)
      const gpuCores = appleMatch[3] ? parseInt(appleMatch[3], 10) : 0; // GPU core count

      // Variant tier values (lower = lower spec)
      const variantTier = {
        'base': 1,   // M1, M2, M3, M4 (no suffix)
        'Pro': 2,    // M1 Pro, M2 Pro, etc.
        'Max': 3,    // M1 Max, M2 Max, etc.
        'Ultra': 4   // M1 Ultra, M2 Ultra, etc.
      };

      const tier = variantTier[variantStr] || 1;

      // Sort formula: generation * 10000 + tier * 1000 + GPU cores
      // Example sort values:
      //   M1 Pro 14C:  1 * 10000 + 2 * 1000 + 14 = 12014
      //   M1 Pro 16C:  1 * 10000 + 2 * 1000 + 16 = 12016
      //   M1 Max 24C:  1 * 10000 + 3 * 1000 + 24 = 13024
      //   M2 8C:       2 * 10000 + 1 * 1000 + 8  = 21008
      //   M2 Pro 10C:  2 * 10000 + 2 * 1000 + 10 = 22010
      return generation * 10000 + tier * 1000 + gpuCores;
    };

    return processors.sort((a, b) => getAppleSiliconSortValue(a) - getAppleSiliconSortValue(b));
  };

  // Check if the selected iPhone model is iPhone 16 or 17 (only allow Unlocked carrier)
  const isIPhone16or17 = () => {
    if (!isIPhone || !cpu) return false;
    const cpuStr = String(cpu);
    // Match "iPhone 16" or "iPhone 17" followed by optional variant (Pro, Pro Max, Plus, etc.)
    return /iPhone\s+(16|17)(\s|$)/i.test(cpuStr);
  };

  const getUniqueSortedValues = (key) => {
    if (!configs || configs.length === 0) return [];
    const values = configs
      .map(row => parseConfig(row[0])[key])
      .filter(value => value !== undefined && value !== null && value !== '');
    return [...new Set(values)].sort();
  };

  const getFilteredUniqueSortedValues = (key, filters) => {
    if (!configs || configs.length === 0) return [];
    const values = configs
      .filter(row => {
        const parsed = parseConfig(row[0]);
        return Object.entries(filters).every(([filterKey, filterValue]) => {
            return !filterValue || parsed[filterKey] === filterValue;
        });
      })
      .map(row => parseConfig(row[0])[key])
      .filter(value => value !== undefined && value !== null && value !== '');
    return [...new Set(values)].sort();
  };

  const parseCapacity = (value) => {
    const match = value.match(/^(\d+(?:\.\d+)?)\s*(TB|GB|MB)$/i);
    if (!match) return Number.MAX_VALUE;
    const num = parseFloat(match[1]);
    const unit = match[2].toUpperCase();
    switch (unit) {
      case 'TB': return num * 1024;
      case 'GB': return num;
      case 'MB': return num / 1024;
      default: return num;
    }
  };

  const years = isMac ? getUniqueSortedValues('year') : [];
  const cpus = isIPhone
    ? sortIPhoneModels(getFilteredUniqueSortedValues('cpu', { year: isMac ? year : '' }))
    : isMac
      ? sortAppleSiliconProcessors(getFilteredUniqueSortedValues('cpu', { year: isMac ? year : '' }))
      : getFilteredUniqueSortedValues('cpu', { year: isMac ? year : '' });
  const screenSizes = hasScreen ? getFilteredUniqueSortedValues('screenSize', { year: isMac ? year : '', cpu }) : [];
  const ssds = getFilteredUniqueSortedValues('ssd', { year: isMac ? year : '', cpu, screenSize: hasScreen ? screenSize : '' })
    .sort((a, b) => parseCapacity(a) - parseCapacity(b));
  const rams = isMac
    ? getFilteredUniqueSortedValues('ram', { year, cpu, screenSize: hasScreen ? screenSize : '', ssd })
        .sort((a, b) => parseCapacity(a) - parseCapacity(b))
    : [];

  // Filter carrier options for iPhone 16/17 to only show "Unlocked"
  const radioOptionsForProduct = isIPad
    ? connectivityOptions
    : (isIPhone
        ? (isIPhone16or17() ? ['Unlocked'] : carrierOptions)
        : []);
  const issueOptions = isPhoneTablet ? phoneTabletIssueOptions : (isMac ? macIssueOptions : []);

  const resetSubsequentFields = (level) => {
    console.log(`Resetting fields from level: ${level}`);
    if (level === 'product') {
      setYear('');
      setCpu('');
      setScreenSize('');
      setSsd('');
      setRam('');
      setRadio('');
      setCondition('');
      setFunctionalCondition('');
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setMpn('');
      setOffer(null);
    } else if (level === 'year') {
      setCpu('');
      setScreenSize('');
      setSsd('');
      setRam('');
      setRadio('');
      setCondition('');
      setFunctionalCondition('');
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setMpn('');
      setOffer(null);
    } else if (level === 'cpu') {
      setScreenSize('');
      setSsd('');
      setRam('');
      setRadio('');
      setCondition('');
      setFunctionalCondition('');
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setMpn('');
      setOffer(null);
    } else if (level === 'screenSize') {
      setSsd('');
      setRam('');
      setRadio('');
      setCondition('');
      setFunctionalCondition('');
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setMpn('');
      setOffer(null);
    } else if (level === 'ssd') {
      setRam('');
      setRadio('');
      setCondition('');
      setFunctionalCondition('');
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setMpn('');
      setOffer(null);
    } else if (level === 'ram') {
      setRadio('');
      setCondition('');
      setFunctionalCondition('');
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setMpn('');
      setOffer(null);
    } else if (level === 'radio') {
      setCondition('');
      setFunctionalCondition('');
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setMpn('');
      setOffer(null);
    } else if (level === 'condition') {
      setFunctionalCondition('');
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setMpn('');
      setOffer(null);
    } else if (level === 'functionalCondition') {
      setIssues([]);
      setAdapter('');
      setBatteryCondition('');
      setOffer(null);
    } else if (level === 'issues') {
      setAdapter('');
      setBatteryCondition('');
      setOffer(null);
    } else if (level === 'adapter') {
      if (!isPhoneTablet) {
        setBatteryCondition('');
      }
      setOffer(null);
    } else if (level === 'batteryCondition') {
      setOffer(null);
    }
  };

  const handleIssueChange = (issue) => {
    setIssues(prev =>
      prev.includes(issue) ? prev.filter(i => i !== issue) : [...prev, issue]
    );
    resetSubsequentFields('issues');
  };

  const handleFunctionalConditionChange = (e) => {
    const value = e.target.value;
    setFunctionalCondition(value);
    if (value === '100% Functional') {
      setIssues([]);
    }
    resetSubsequentFields('functionalCondition');
  };

  const handleGetOffer = async () => {
    if (condition === 'Brand New, Factory Sealed') {
      navigate('/offer', {
        state: {
          deviceDetails: {
            product,
            cpu,
            screenSize,
            ssd,
            ram,
            radio,
            condition,
            functionalCondition,
            issues,
            adapter,
            batteryCondition,
            conditionDescription: conditionDescriptions[condition],
            mpn,
          },
          requestFlow: true
        }
      });
      return;
    }
    console.log('--- Starting Get Offer ---');
    let fullConfig;
    let fetchConfig;

    if (isMac) {
      const cpuBase = cpu.includes('-') ? cpu.split('-')[0] : cpu;
      const gpuCores = cpu.includes('-') ? cpu.split('-')[1] : '';
      const productPart = product;
      const formattedScreenSize = hasScreen && screenSize ? `${screenSize}"` : '';

      let parts = [year, productPart];
      if (hasScreen) parts.push(formattedScreenSize);
      parts.push(cpuBase);
      parts.push(ram);
      parts.push(ssd);
      if (gpuCores) parts.push(gpuCores);

      fullConfig = parts.join('-');
      fetchConfig = fullConfig;
    } else if (isIPad) {
      fullConfig = `${cpu}-${screenSize}-${ssd}-${radio}`;
      fetchConfig = `${cpu}-${screenSize}-${ssd}-Wifi Only`;
    } else if (isIPhone) {
      fullConfig = `${cpu}-${ssd}-${radio}`;
      fetchConfig = `${cpu}-${ssd}-Unlocked`;
    } else {
      console.error("Cannot determine config format for unknown product:", product);
      setOffer('Error');
      return;
    }

    console.log('Selected Device Config (User Input):', fullConfig);
    console.log('Config used for API lookup:', fetchConfig);

    const params = new URLSearchParams({
      config: fetchConfig,
      condition: condition,
      product: product,
      adapter: adapter,
      functionalCondition: functionalCondition,
      issues: issues.join(','),
      batteryCondition
    });
    const url = `${BACKEND_URL}/get-offer?${params.toString()}`;
    console.log('Fetching offer from URL:', url);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Fetch failed: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`Fetch failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('Offer API response data:', data);
      const priceString = data.price?.toString().trim() || '';
      const numericPrice = parseFloat(priceString.replace(/[$,]/g, ''));
      if (!priceString.startsWith('$') || isNaN(numericPrice)) {
        navigate('/offer', {
          state: {
            deviceDetails: {
              product,
              cpu,
              screenSize,
              ssd,
              ram,
              radio,
              condition,
              functionalCondition,
              issues,
              adapter,
              batteryCondition,
              conditionDescription: conditionDescriptions[condition],
              mpn,
            },
            requestFlow: true,
          },
        });
        return;
      }

      let basePrice = (data.price && data.price !== 'Not found')
                      ? parseFloat(String(data.price).replace(/[$,]/g, ''))
                      : 'Not Found';
      let finalPrice = basePrice;

      if (basePrice !== 'Not Found') {
        if (isIPad && radio === 'Cellular + Wifi') {
          finalPrice = basePrice * 1.10;
          console.log(`Applied +10% for iPad Cellular. Base: $${basePrice.toFixed(2)}, Adjusted: $${finalPrice.toFixed(2)}`);
        } else if (isIPhone) {
          if (radio === 'AT&T' || radio === 'Verizon') {
            finalPrice = basePrice * 0.85;
            console.log(`Applied -15% for iPhone AT&T/Verizon. Base: $${basePrice.toFixed(2)}, Adjusted: $${finalPrice.toFixed(2)}`);
          } else if (radio === 'Sprint' || radio === 'T-Mobile') {
            finalPrice = basePrice * 0.65;
            console.log(`Applied -35% for iPhone Sprint/T-Mobile. Base: $${basePrice.toFixed(2)}, Adjusted: $${finalPrice.toFixed(2)}`);
          }
        }

        finalPrice = `$${Math.round(finalPrice)}`;
      } else {
        finalPrice = 'Not Found';
      }

      console.log('Final calculated offer price:', finalPrice);
      setOffer(finalPrice);

      window.dataLayer = window.dataLayer || [];
      console.log('Pushing offerPageDisplayed event to dataLayer');
      window.dataLayer.push({ event: 'offerPageDisplayed' });

      console.log('Navigating to /offer page');
      navigate('/offer', {
        state: {
          offerPrice: finalPrice,
          deviceDetails: {
            product,
            cpu,
            screenSize,
            ssd,
            ram,
            radio,
            condition,
            functionalCondition,
            issues,
            adapter,
            batteryCondition,
            conditionDescription: conditionDescriptions[condition],
            mpn,
          }
        }
      });

    } catch (err) {
      console.error('handleGetOffer error:', err);
      setOffer('Not Found');

      window.dataLayer = window.dataLayer || [];
      console.log('Pushing offerPageDisplayed event to dataLayer (error case)');
      window.dataLayer.push({ event: 'offerPageDisplayed', offerError: true });

      navigate('/offer', {
        state: {
          offerPrice: 'Not Found',
          deviceDetails: {
            product,
            cpu,
            screenSize,
            ssd,
            ram,
            radio,
            condition,
            functionalCondition,
            issues,
            adapter,
            batteryCondition,
            conditionDescription: conditionDescriptions[condition],
            mpn,
          }
        }
      });
    }
  };

  const isOfferButtonEnabled = () => {
    if (condition === 'Brand New, Factory Sealed') {
      return true;
    }
    if (!adapter) return false;
    if (functionalCondition === 'Has Functional Issues' && issues.length === 0) {
      return false;
    }
    if (isPhoneTablet && !batteryCondition) return false;
    if (isMac) {
      return !!(product && year && cpu && ssd && ram && condition && functionalCondition && adapter);
    } else if (isIPad) {
       return !!(product && cpu && screenSize && ssd && radio && condition && functionalCondition && adapter && batteryCondition);
    } else if (isIPhone) {
       return !!(product && cpu && ssd && radio && condition && functionalCondition && adapter && batteryCondition);
    }
    return false;
  };

  return (
    <Box sx={{ padding: '20px', maxWidth: '600px', margin: 'auto' }}>
      <Typography variant="h4" align="center" gutterBottom>
        Sell Your {product || 'Device'}
      </Typography>

      {!isProductPreselected && (
        <Select
          value={product}
          onChange={(e) => {
            setProduct(e.target.value);
            resetSubsequentFields('product');
          }}
          fullWidth
          displayEmpty
          style={{ marginTop: '20px' }}
          aria-label="Select Product Type"
        >
          <MenuItem value="" disabled>Select Product Type</MenuItem>
          {products.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </Select>
      )}

      {isLoadingConfigs && (
        <Typography style={{ marginTop: '20px', textAlign: 'center' }}>
          Loading configurations for {product}...
        </Typography>
      )}

      {product && !isLoadingConfigs && (
        <>
          {isMac && (
            <Select value={year} onChange={(e) => { setYear(e.target.value); resetSubsequentFields('year'); }} fullWidth displayEmpty style={{ marginTop: '20px' }} disabled={years.length === 0} aria-label="Select Year">
              <MenuItem value="" disabled>Select Year</MenuItem>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          )}

          <Select value={cpu} onChange={(e) => { setCpu(e.target.value); resetSubsequentFields('cpu'); }} fullWidth displayEmpty style={{ marginTop: '20px' }} disabled={!product || (isMac && !year) || cpus.length === 0} aria-label={isMac ? 'Select Processor' : 'Select Model'}>
            <MenuItem value="" disabled>{isMac ? 'Select Processor' : 'Select Model'}</MenuItem>
            {cpus.map(c => <MenuItem key={c} value={c}>{c.replace('-', ' ')}</MenuItem>)}
          </Select>

          {hasScreen && (
            <Select value={screenSize} onChange={(e) => { setScreenSize(e.target.value); resetSubsequentFields('screenSize'); }} fullWidth displayEmpty style={{ marginTop: '20px' }} disabled={!cpu || screenSizes.length === 0} aria-label={isIPad ? 'Select Screen Size & Generation' : 'Select Screen Size'}>
              <MenuItem value="" disabled>{isIPad ? 'Select Screen & Gen' : 'Select Screen Size'}</MenuItem>
              {screenSizes.map(s => <MenuItem key={s} value={s}>{isIPad ? s : `${s}"`}</MenuItem>)}
            </Select>
          )}

          <Select value={ssd} onChange={(e) => { setSsd(e.target.value); resetSubsequentFields('ssd'); }} fullWidth displayEmpty style={{ marginTop: '20px' }} disabled={!cpu || (hasScreen && !screenSize) || ssds.length === 0} aria-label="Select Storage">
            <MenuItem value="" disabled>Select Storage</MenuItem>
            {ssds.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>

          {isMac && (
            <Select value={ram} onChange={(e) => { setRam(e.target.value); resetSubsequentFields('ram'); }} fullWidth displayEmpty style={{ marginTop: '20px' }} disabled={!ssd || rams.length === 0} aria-label="Select Memory">
              <MenuItem value="" disabled>Select Memory</MenuItem>
              {rams.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          )}

          {isPhoneTablet && (
            <Select value={radio} onChange={(e) => { setRadio(e.target.value); resetSubsequentFields('radio'); }} fullWidth displayEmpty style={{ marginTop: '20px' }} disabled={!ssd || radioOptionsForProduct.length === 0} aria-label={isIPad ? 'Select Connectivity' : 'Select Carrier'}>
              <MenuItem value="" disabled>{isIPad ? 'Select Connectivity' : 'Select Carrier'}</MenuItem>
              {radioOptionsForProduct.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          )}

          <Select
            value={condition}
            onChange={(e) => { setCondition(e.target.value); resetSubsequentFields('condition'); }}
            fullWidth displayEmpty style={{ marginTop: '20px' }}
            disabled={!( (isMac && ram) || (isIPad && radio) || (isIPhone && radio) )}
            aria-label="Select Cosmetic Condition"
          >
            <MenuItem value="" disabled>Select Cosmetic Condition</MenuItem>
            {Object.keys(conditionDescriptions).map(condKey => (
              <MenuItem key={condKey} value={condKey}>{condKey}</MenuItem>
            ))}
          </Select>
          {condition && (
            <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', mt: 1, mb: 1, fontStyle: 'italic' }}>
              {conditionDescriptions[condition]}
            </Typography>
          )}
          {condition === 'Brand New, Factory Sealed' && (
            <>
              <TextField
                label="MPN/Part Number:"
                value={mpn}
                onChange={(e) => setMpn(e.target.value)}
                fullWidth
                placeholder="Example: MX2W3LL/A"
                style={{ marginTop: '20px' }}
                aria-label="MPN or Part Number"
              />
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', mt: 1, mb: 1, fontStyle: 'italic' }}>
                The MPN is printed on lower left corner on the back of the retail box and will start with letter "M" like MX2W3LL/A.
              </Typography>
            </>
          )}

          {condition !== 'Brand New, Factory Sealed' && (
            <>
              <Select
                value={functionalCondition}
                onChange={handleFunctionalConditionChange}
                fullWidth
                displayEmpty
                style={{ marginTop: '20px' }}
                disabled={!condition}
                aria-label="Select Functional Condition"
              >
                <MenuItem value="" disabled>Select Functional Condition</MenuItem>
                <MenuItem value="100% Functional">100% Functional</MenuItem>
                <MenuItem value="Has Functional Issues">Has Functional Issues</MenuItem>
              </Select>

              {functionalCondition === 'Has Functional Issues' && (
                <Box
                  sx={{
                    marginTop: '20px',
                    padding: '15px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                    PLEASE CHECK ALL APPLICABLE DEVICE ISSUES:
                  </Typography>
                  {issueOptions.length > 0 ? issueOptions.map(issue => (
                    <Box key={issue} sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={issues.includes(issue)}
                            onChange={() => handleIssueChange(issue)}
                            sx={{ padding: '5px' }}
                            aria-labelledby={`issue-label-${issue.replace(/\s+/g, '-')}`}
                          />
                        }
                        label={<Typography id={`issue-label-${issue.replace(/\s+/g, '-')}`}>{issue}</Typography>}
                        sx={{ margin: 0 }}
                      />
                    </Box>
                  )) : (
                    <Typography>No specific issues listed for this product type.</Typography>
                  )}
                </Box>
              )}

              {isPhoneTablet && (
                <Select
                  value={batteryCondition}
                  onChange={(e) => { setBatteryCondition(e.target.value); resetSubsequentFields('batteryCondition'); }}
                  fullWidth
                  displayEmpty
                  style={{ marginTop: '20px' }}
                  disabled={
                    !functionalCondition ||
                    (functionalCondition === 'Has Functional Issues' && issues.length === 0)
                  }
                  aria-label="Select Battery Condition"
                >
                  <MenuItem value="" disabled>Select Battery Condition</MenuItem>
                  {batteryConditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              )}

              <Select
                value={adapter}
                onChange={(e) => { setAdapter(e.target.value); resetSubsequentFields('adapter'); }}
                fullWidth
                displayEmpty
                style={{ marginTop: '20px' }}
                disabled={
                  isPhoneTablet
                    ? !batteryCondition
                    : !functionalCondition || (functionalCondition === 'Has Functional Issues' && issues.length === 0)
                }
                aria-label="Power Supply Included"
              >
                <MenuItem value="" disabled>Power Supply</MenuItem>
                <MenuItem value="Genuine Apple Charger Included">Included (Genuine Apple)</MenuItem>
                <MenuItem value="Not Included">Not Included</MenuItem>
              </Select>
            </>
          )}

          <Button
            variant="contained"
            onClick={handleGetOffer}
            disabled={!isOfferButtonEnabled()}
            sx={{
              backgroundColor: '#1976d2',
              color: '#fff',
              fontSize: '18px',
              fontWeight: 600,
              height: '56px',
              borderRadius: '12px',
              textTransform: 'none',
              mt: 4,
              width: '100%',
              boxShadow: '0 4px 14px rgba(25, 118, 210, 0.25)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#1565c0',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(25, 118, 210, 0.35)',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999',
                transform: 'none',
                boxShadow: 'none',
              },
            }}
            aria-label="Get Offer for selected device"
          >
            Get Offer
          </Button>
        </>
      )}
    </Box>
  );
}

function AppWrapper() {
  console.log('Rendering AppWrapper (Router Setup)');
  return (
    <Router>
      <GlobalStyle />
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/offer" element={<OfferPage />} />
      </Routes>
    </Router>
  );
}

export default AppWrapper;